import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Flame, Play, Square, Waves } from "lucide-react";

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function Craving() {
  const t = useT();
  const [active, setActive] = useState(null); // { id, started_at }
  const [seconds, setSeconds] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const sessions = useQuery({
    queryKey: ["craving-sessions"],
    queryFn: async () => (await api.get("/craving/sessions")).data,
    retry: false,
  });

  useEffect(() => {
    if (!active) return;
    timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer.current);
  }, [active]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/craving/start", {});
      const id = r.data?.id || r.data?.session_id || `local-${Date.now()}`;
      setActive({ id, started_at: new Date().toISOString() });
      setSeconds(0);
      toast.success(t("client.startCraving"));
    } catch (e) { toast.error(errMsg(e, t("errors.generic"))); }
    finally { setBusy(false); }
  };

  const end = async () => {
    setBusy(true);
    try {
      await api.post("/craving/end", { id: active?.id, note: note.trim() || null, duration_s: seconds });
      toast.success(t("client.endCraving"));
      setActive(null);
      setNote("");
      setSeconds(0);
      sessions.refetch();
    } catch (e) { toast.error(errMsg(e, t("errors.generic"))); }
    finally { setBusy(false); }
  };

  const items = Array.isArray(sessions.data) ? sessions.data : sessions.data?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("client.craving")} title={t("client.startCraving")} subtitle={t("brand.tagline")} />

      <div className="rounded-3xl bg-white shadow-card p-6 md:p-8 flex flex-col items-center text-center" data-testid="craving-console">
        <div className={`h-24 w-24 rounded-full flex items-center justify-center mb-4 transition ${active ? "bg-clay-500/20" : "bg-cream-100"}`}>
          <Flame className={`h-10 w-10 ${active ? "text-clay-600 animate-pulse" : "text-ink-500"}`} strokeWidth={1.5} />
        </div>
        <div className="font-heading text-5xl text-ink-900 tracking-tight font-mono" data-testid="craving-timer">
          {active ? fmt(seconds) : "0:00"}
        </div>
        <p className="text-sm text-ink-500 mt-2 max-w-md">
          {active
            ? "Breathe. This wave will crest and fall. Stay with the timer — you're doing enough."
            : "When the wave rises, start a session. The urge peaks in minutes, not hours."}
        </p>

        {active ? (
          <div className="w-full max-w-md mt-6 space-y-3">
            <Textarea
              data-testid="craving-note"
              placeholder="What's rising for you right now?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="rounded-xl bg-cream-50"
            />
            <Button onClick={end} disabled={busy} data-testid="craving-end" className="w-full rounded-full h-11 bg-sage-500 hover:bg-sage-600">
              <Square className="h-4 w-4 mr-1.5" /> {t("client.endCraving")}
            </Button>
          </div>
        ) : (
          <Button onClick={start} disabled={busy} data-testid="craving-start" className="mt-6 rounded-full h-12 px-8 bg-clay-500 hover:bg-clay-600 text-white">
            <Play className="h-4 w-4 mr-1.5" /> {t("client.startCraving")}
          </Button>
        )}
      </div>

      <section>
        <h2 className="font-heading text-xl text-ink-900 mb-4">Recent sessions</h2>
        {sessions.isLoading && <LoadingCard height="h-24" />}
        {!sessions.isLoading && items.length === 0 && (
          <EmptyState icon={Waves} title={t("common.empty")} testId="craving-sessions-empty" />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="craving-sessions">
          {items.map((s, i) => (
            <div key={s.id || i} className="rounded-2xl bg-white shadow-card p-4">
              <div className="text-xs text-ink-500">{s.created_at && new Date(s.created_at).toLocaleString()}</div>
              <div className="font-mono text-ink-900 mt-1">
                {typeof s.duration_s === "number" ? fmt(s.duration_s) : (s.duration || "—")}
              </div>
              {s.note && <div className="text-sm text-ink-700 mt-2 line-clamp-3">{s.note}</div>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
